import React, { useState, useEffect } from "react";
import {
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";
import imgUrl from "../assets/images/imgUrl";
import { apiServices } from "../services/apiServices"; 

const Contact = () => {
  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    country: '',
    mobile: '',
    designation: '',
    email: '',
    subject: '',
    message: ''
  });

  // UI state
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });
  const [errors, setErrors] = useState({});
  
  // Countries state
  const [countries, setCountries] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);

  // Fetch countries from API
  const fetchCountries = async () => {
    try {
      setLoadingCountries(true);
      const response = await apiServices.getCountries();
      if (response.data.countries && Array.isArray(response.data.countries)) {
        setCountries(response.data.countries);
      } else {
        console.error('Invalid countries data format:', response.data);
        // Fallback to basic countries list
        setCountries([
          { ids: 1, countryname: "USA", alpha_2_code: "US", alpha_3_code: "USA", numeric_code: "840", counter: 1 },
          { ids: 2, countryname: "India", alpha_2_code: "IN", alpha_3_code: "IND", numeric_code: "356", counter: 2 },
          { ids: 3, countryname: "UK", alpha_2_code: "GB", alpha_3_code: "GBR", numeric_code: "826", counter: 3 },
          { ids: 4, countryname: "Germany", alpha_2_code: "DE", alpha_3_code: "DEU", numeric_code: "276", counter: 4 },
          { ids: 5, countryname: "Australia", alpha_2_code: "AU", alpha_3_code: "AUS", numeric_code: "036", counter: 5 },
          { ids: 6, countryname: "Canada", alpha_2_code: "CA", alpha_3_code: "CAN", numeric_code: "124", counter: 6 },
          { ids: 7, countryname: "France", alpha_2_code: "FR", alpha_3_code: "FRA", numeric_code: "250", counter: 7 },
          { ids: 8, countryname: "Japan", alpha_2_code: "JP", alpha_3_code: "JPN", numeric_code: "392", counter: 8 }
        ]);
      }
    } catch (error) {
      console.error('Error fetching countries:', error);
      // Fallback to basic countries list
      setCountries([
        { ids: 1, countryname: "USA", alpha_2_code: "US", alpha_3_code: "USA", numeric_code: "840", counter: 1 },
        { ids: 2, countryname: "India", alpha_2_code: "IN", alpha_3_code: "IND", numeric_code: "356", counter: 2 },
        { ids: 3, countryname: "UK", alpha_2_code: "GB", alpha_3_code: "GBR", numeric_code: "826", counter: 3 },
        { ids: 4, countryname: "Germany", alpha_2_code: "DE", alpha_3_code: "DEU", numeric_code: "276", counter: 4 },
        { ids: 5, countryname: "Australia", alpha_2_code: "AU", alpha_3_code: "AUS", numeric_code: "036", counter: 5 },
        { ids: 6, countryname: "Canada", alpha_2_code: "CA", alpha_3_code: "CAN", numeric_code: "124", counter: 6 },
        { ids: 7, countryname: "France", alpha_2_code: "FR", alpha_3_code: "FRA", numeric_code: "250", counter: 7 },
        { ids: 8, countryname: "Japan", alpha_2_code: "JP", alpha_3_code: "JPN", numeric_code: "392", counter: 8 }
      ]);
    } finally {
      setLoadingCountries(false);
    }
  };

  // Load countries on component mount
  useEffect(() => {
    fetchCountries();
  }, []);

  // Handle input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    
    if (!formData.country || formData.country === '') {
      newErrors.country = 'Country is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setAlert({ show: false, type: '', message: '' });
    
    try {
      const response = await apiServices.submitContactForm(formData);
      console.log(response, "response");
      if (response.status === 200 || response.status === 202) {
        setAlert({
          show: true,
          type: 'success',
          message: 'Contact form submitted successfully! We will get back to you soon.'
        });
        
        // Reset form
        setFormData({
          firstName: '',
          lastName: '',
          country: '',
          mobile: '',
          designation: '',
          email: '',
          subject: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Contact form submission error:', error);
      
      let errorMessage = 'Failed to submit contact form. Please try again.';
      
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.status === 400) {
        errorMessage = 'Please check your form data and try again.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      }
      
      setAlert({
        show: true,
        type: 'error',
        message: errorMessage
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/*Contact Home section */}
      <Box
        className="contactSectionBg commonBannerBg"
        sx={{
          backgroundSize: "cover",
          backgroundPosition: "center",
          //   position: "relative",
          color: "white",
          px: 2,
        }}
        mb={5}
      >
        {/* Mini Header (Top-Left) */}
        <Box
          sx={{
            position: "absolute",
            top: 80,
            left: 0,
            display: "flex",
            alignItems: "center",
          }}
        >
          <Box
            component="img"
            src={imgUrl.doveImg}
            alt="Peace Dove Logo"
            sx={{ maxWidth: 200, height: "auto", mr: 1 }}
          />
        </Box>
      </Box>
      {/* Contact Button */}
      <Box className="contactButton d-flex-align-center-justify-center">
        <Grid container spacing={0}>
          <Grid size={12}>
          <a href="mailto:peace@i-am-peacekeeper.com">
            <Button variant="contained" className="blueButton">
              peace@i-am-peacekeeper.com
            </Button>
            </a>
          </Grid>
        </Grid>
      </Box>

      {/* Contact Heading */}
      {/* directors section  */}
      <Box className="d-flex-align-center-justify-center" mt={5} mb={1}>
        <Grid container direction="column" alignItems="center" spacing={2}>
          <Grid size={12}>
            <Typography variant="h5" className="founder-title mainHeading">
              Contact Form
            </Typography>
          </Grid>
        </Grid>
      </Box>
      {/* contact form section box */}
      <Box
        sx={{
          backgroundColor: "#D9E8E4",
          padding: 4,
          maxWidth: 600,
          margin: "0 auto",
          borderRadius: 2,
        }}
        className="contactMainSection"
        mt={5}
      >
        {/* Alert Messages */}
        {alert.show && (
          <Alert 
            severity={alert.type} 
            sx={{ mb: 2 }}
            onClose={() => setAlert({ show: false, type: '', message: '' })}
          >
            {alert.message}
          </Alert>
        )}

        <form className="contactForm" onSubmit={handleSubmit}>
            <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
                <TextField 
                  fullWidth 
                  label="" 
                  placeholder="First Name" 
                  variant="outlined" 
                  size="small"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  error={!!errors.firstName}
                  helperText={errors.firstName}
                  required
                />
            </Grid>
             <Grid size={{ xs: 12, md: 6 }}>
                <TextField 
                  fullWidth 
                  label="" 
                  placeholder="Last Name" 
                  variant="outlined" 
                  size="small"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  error={!!errors.lastName}
                  helperText={errors.lastName}
                  required
                />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth error={!!errors.country}>
            <Select 
              value={formData.country} 
              size="small"
              onChange={(e) => handleInputChange('country', e.target.value)}
              displayEmpty
              disabled={loadingCountries}
            >
                <MenuItem value="" disabled>
                  {loadingCountries ? "Loading countries..." : "Select Country"}
                </MenuItem>
                {countries.map((country, i) => (
                <MenuItem key={country.ids || i} value={country.countryname || country}>
                    {country.countryname || country}
                </MenuItem>
                ))}
            </Select>
            {errors.country && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                {errors.country}
              </Typography>
            )}
            </FormControl>

            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
                <TextField 
                  fullWidth 
                  label="" 
                  placeholder="Mobile (optional)" 
                  variant="outlined" 
                  size="small"
                  value={formData.mobile}
                  onChange={(e) => handleInputChange('mobile', e.target.value)}
                />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
                <TextField 
                  fullWidth 
                  label="" 
                  placeholder="Designation" 
                  variant="outlined" 
                  size="small"
                  value={formData.designation}
                  onChange={(e) => handleInputChange('designation', e.target.value)}
                />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
                <TextField 
                  fullWidth 
                  label="" 
                  placeholder="Email" 
                  variant="outlined" 
                  size="small"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  error={!!errors.email}
                  helperText={errors.email}
                  required
                />
            </Grid>

            <Grid size={12}>
                <TextField 
                  fullWidth 
                  label="" 
                  placeholder="Subject" 
                  variant="outlined" 
                  size="small"
                  value={formData.subject}
                  onChange={(e) => handleInputChange('subject', e.target.value)}
                  error={!!errors.subject}
                  helperText={errors.subject}
                  required
                />
            </Grid>

            <Grid size={12}>
                <TextField 
                  fullWidth 
                  label="Message" 
                  multiline 
                  rows={4} 
                  variant="outlined" 
                  size="small"
                  value={formData.message}
                  onChange={(e) => handleInputChange('message', e.target.value)}
                  error={!!errors.message}
                  helperText={errors.message}
                  required
                />
            </Grid>

            <Grid size={12} textAlign="center">
                <Button
                type="submit"
                variant="contained"
                disabled={loading}
                sx={{
                    background: "linear-gradient(to right, #006389, #399847)",
                    color: "#fff",
                    borderRadius:"50px",
                    fontWeight:"bold",
                    px: 3,
                    textTransform:"capitalize",
                    fontSize:"20px"
                }}
                className="contactSubmitButton"
                >
                {loading ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                    SUBMITTING...
                  </>
                ) : (
                  'SUBMIT'
                )}
                </Button>
            </Grid>
            </Grid>

        </form>
      </Box>
    </>
  );
};

export default Contact;
